import { Magnet, Shell } from 'lucide-react';
import type { ExposureType } from '@/store/heatmapStore';

interface ExposureToggleProps {
  type: ExposureType;
  onTypeChange: (type: ExposureType) => void;
}

export function ExposureToggle({ type, onTypeChange }: ExposureToggleProps) {
  return (
    <div
      role="group"
      aria-label="Exposure type"
      className="inline-flex h-10 shrink-0 gap-0.5 rounded-md border border-line-strong bg-panel p-[3px]"
    >
      <ToggleButton type="gex" isActive={type === 'gex'} onClick={() => onTypeChange('gex')} />
      <ToggleButton type="vex" isActive={type === 'vex'} onClick={() => onTypeChange('vex')} />
    </div>
  );
}

function ToggleButton({
  type,
  isActive,
  onClick,
}: {
  type: ExposureType;
  isActive: boolean;
  onClick: () => void;
}) {
  const isGex = type === 'gex';
  const Icon = isGex ? Magnet : Shell;
  const activeIconClassName = isGex
    ? 'text-[#fbbf24] drop-shadow-[0_0_4px_rgba(250,204,21,0.5)]'
    : 'text-[#7dd3fc] drop-shadow-[0_0_4px_rgba(125,211,252,0.35)]';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      title={isGex ? 'GEX: Gamma Exposure' : 'VEX: Volatility Exposure'}
      className={`inline-flex items-center gap-[7px] rounded px-3.5 font-mono text-[13px] font-semibold tracking-[0.04em] min-[721px]:px-4 ${
        isActive
          ? 'bg-white/10 text-fg shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
          : 'text-subtle hover:text-fg'
      }`}
    >
      <Icon
        aria-hidden="true"
        className={`h-3.5 w-3.5 transition-[color,filter] duration-300 ${isActive ? activeIconClassName : ''}`}
        strokeWidth={2}
      />
      {type.toUpperCase()}
    </button>
  );
}
